import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, RefreshCw, FlaskConical, ClipboardPaste, Wand2 } from "lucide-react";
import {
  listStripeTestRuns,
  upsertStripeTestRun,
  deleteStripeTestRun,
} from "@/lib/stripe-test-runs.functions";
import { parseStripeCliOutput, type ParsedStripeCli } from "@/lib/stripe-cli-parser";

export const Route = createFileRoute("/_authenticated/admin/stripe-tests")({
  head: () => ({
    meta: [
      { title: "Stripe end-to-end test log — Peopels admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StripeTestsPage,
});

type Run = Awaited<ReturnType<typeof listStripeTestRuns>>["items"][number];

const PAGE_SIZE = 25;

const EMPTY: Partial<Run> & { title: string } = {
  title: "",
  environment: "test",
  status: "in_progress",
  notes: "",
  webhook_event_ids: [],
};

function nowIsoLocal() {
  return new Date().toISOString();
}

function fmt(v: string | null | undefined) {
  if (!v) return "—";
  const d = new Date(v);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function StripeTestsPage() {
  const qc = useQueryClient();
  const list = useServerFn(listStripeTestRuns);
  const upsert = useServerFn(upsertStripeTestRun);
  const del = useServerFn(deleteStripeTestRun);

  const [statusFilter, setStatusFilter] = useState<"all" | "in_progress" | "passed" | "failed">("all");
  const [page, setPage] = useState(0);
  const [form, setForm] = useState<Partial<Run> & { title: string }>(EMPTY);
  const editing = Boolean(form.id);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["stripe-tests", statusFilter, page],
    queryFn: () => list({ data: { status: statusFilter, limit: PAGE_SIZE, offset: page * PAGE_SIZE } }),
    placeholderData: keepPreviousData,
  });

  const saveMut = useMutation({
    mutationFn: (payload: Partial<Run> & { title: string }) => upsert({ data: payload as never }),
    onSuccess: () => {
      toast.success(editing ? "Test run updated" : "Test run recorded");
      setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["stripe-tests"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not save"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["stripe-tests"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete"),
  });

  const items = (data?.items ?? []) as Run[];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const stamp = (k: keyof Run) => set(k as never, nowIsoLocal() as never);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin/kyc" className="text-sm text-muted-foreground hover:text-foreground">← Back to admin</Link>
            <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold">
              <FlaskConical className="h-5 w-5" /> Stripe end-to-end test log
            </h1>
            <p className="text-sm text-muted-foreground">
              Record Checkout → webhook → booking state → Connect transfer/payout test runs.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
            Refresh
          </Button>
        </div>

        {/* Form */}
        <section className="mb-8 rounded-2xl border bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold">
            <Plus className="h-4 w-4" /> {editing ? "Edit test run" : "New test run"}
          </h2>

          <PasteStripeCli
            onParsed={(parsed, mode) => {
              setForm((f) => {
                const next: typeof f = { ...f };
                (Object.keys(parsed) as Array<keyof ParsedStripeCli>).forEach((k) => {
                  const incoming = parsed[k] as unknown;
                  if (incoming === undefined || incoming === null) return;
                  if (Array.isArray(incoming) && incoming.length === 0) return;
                  const current = (f as Record<string, unknown>)[k];
                  const isEmpty =
                    current === undefined || current === null || current === "" ||
                    (Array.isArray(current) && current.length === 0);
                  if (mode === "overwrite" || isEmpty) {
                    (next as Record<string, unknown>)[k] = incoming;
                  }
                });
                return next;
              });
            }}
          />


          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Title" required>
              <Input value={form.title ?? ""} onChange={(e) => set("title", e.target.value)} placeholder="2026-06-16 Connect smoke test" />
            </Field>
            <Field label="Environment">
              <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.environment ?? "test"} onChange={(e) => set("environment", e.target.value as Run["environment"])}>
                <option value="test">Test mode</option>
                <option value="live">Live mode</option>
              </select>
            </Field>
            <Field label="Status">
              <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.status ?? "in_progress"} onChange={(e) => set("status", e.target.value as Run["status"])}>
                <option value="in_progress">In progress</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </select>
            </Field>
            <Field label="Booking id (uuid)">
              <Input value={form.booking_id ?? ""} onChange={(e) => set("booking_id", e.target.value || null)} placeholder="00000000-0000-…" />
            </Field>

            <Field label="Checkout session id"><Input value={form.checkout_session_id ?? ""} onChange={(e) => set("checkout_session_id", e.target.value || null)} placeholder="cs_test_…" /></Field>
            <Field label="Payment intent id"><Input value={form.payment_intent_id ?? ""} onChange={(e) => set("payment_intent_id", e.target.value || null)} placeholder="pi_…" /></Field>
            <Field label="Charge id"><Input value={form.charge_id ?? ""} onChange={(e) => set("charge_id", e.target.value || null)} placeholder="ch_…" /></Field>
            <Field label="Connected account id"><Input value={form.connected_account_id ?? ""} onChange={(e) => set("connected_account_id", e.target.value || null)} placeholder="acct_…" /></Field>
            <Field label="Transfer id"><Input value={form.transfer_id ?? ""} onChange={(e) => set("transfer_id", e.target.value || null)} placeholder="tr_…" /></Field>
            <Field label="Payout id"><Input value={form.payout_id ?? ""} onChange={(e) => set("payout_id", e.target.value || null)} placeholder="po_…" /></Field>

            <Field label="Webhook event ids (comma-separated)" full>
              <Input
                value={(form.webhook_event_ids ?? []).join(", ")}
                onChange={(e) => set("webhook_event_ids", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="evt_…, evt_…"
              />
            </Field>

            <Field label="Booking status before"><Input value={form.booking_status_before ?? ""} onChange={(e) => set("booking_status_before", e.target.value || null)} placeholder="pending_payment" /></Field>
            <Field label="Booking status after"><Input value={form.booking_status_after ?? ""} onChange={(e) => set("booking_status_after", e.target.value || null)} placeholder="confirmed" /></Field>
            <Field label="Payment status before"><Input value={form.payment_status_before ?? ""} onChange={(e) => set("payment_status_before", e.target.value || null)} placeholder="unpaid" /></Field>
            <Field label="Payment status after"><Input value={form.payment_status_after ?? ""} onChange={(e) => set("payment_status_after", e.target.value || null)} placeholder="paid" /></Field>

            <Field label="Amount total (pence)"><Input type="number" value={form.amount_total_pence ?? ""} onChange={(e) => set("amount_total_pence", e.target.value === "" ? null : Number(e.target.value))} /></Field>
            <Field label="Application fee (pence)"><Input type="number" value={form.application_fee_pence ?? ""} onChange={(e) => set("application_fee_pence", e.target.value === "" ? null : Number(e.target.value))} /></Field>
            <Field label="Helper payout (pence)"><Input type="number" value={form.helper_payout_pence ?? ""} onChange={(e) => set("helper_payout_pence", e.target.value === "" ? null : Number(e.target.value))} /></Field>

            {([
              ["checkout_created_at", "Checkout created"],
              ["checkout_completed_at", "Checkout completed"],
              ["webhook_received_at", "Webhook received"],
              ["booking_state_changed_at", "Booking state changed"],
              ["transfer_created_at", "Transfer created"],
              ["payout_paid_at", "Payout paid"],
            ] as const).map(([k, label]) => (
              <Field key={k} label={label}>
                <div className="flex gap-2">
                  <Input
                    type="datetime-local"
                    value={form[k] ? new Date(form[k] as string).toISOString().slice(0, 16) : ""}
                    onChange={(e) => set(k as never, (e.target.value ? new Date(e.target.value).toISOString() : null) as never)}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => stamp(k)}>Now</Button>
                </div>
              </Field>
            ))}

            <Field label="Notes" full>
              <Textarea rows={3} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} placeholder="Card used, anomalies, follow-ups…" />
            </Field>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            {editing ? <Button variant="outline" onClick={() => setForm(EMPTY)}>Cancel edit</Button> : null}
            <Button
              onClick={() => saveMut.mutate(form)}
              disabled={saveMut.isPending || !form.title || form.title.trim().length < 2}
            >
              {saveMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {editing ? "Save changes" : "Record test run"}
            </Button>
          </div>
        </section>

        {/* List */}
        <div className="mb-3 flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => { setPage(0); setStatusFilter(e.target.value as typeof statusFilter); }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="in_progress">In progress</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
          </select>
          <span className="text-xs text-muted-foreground">{total} run{total === 1 ? "" : "s"}</span>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border bg-white p-10 text-center text-sm text-muted-foreground">
            No test runs recorded yet.
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => (
              <li key={r.id} className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{r.title}</p>
                    <p className="text-xs text-muted-foreground">{fmt(r.created_at)} · {r.environment}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={r.status === "passed" ? "default" : r.status === "failed" ? "destructive" : "secondary"}>
                      {r.status}
                    </Badge>
                    <Button size="sm" variant="outline" onClick={() => setForm(r)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm("Delete this test run?")) delMut.mutate(r.id); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                  {r.checkout_session_id ? <Row k="Checkout session" v={r.checkout_session_id} /> : null}
                  {r.payment_intent_id ? <Row k="Payment intent" v={r.payment_intent_id} /> : null}
                  {r.charge_id ? <Row k="Charge" v={r.charge_id} /> : null}
                  {r.connected_account_id ? <Row k="Connected account" v={r.connected_account_id} /> : null}
                  {r.transfer_id ? <Row k="Transfer" v={r.transfer_id} /> : null}
                  {r.payout_id ? <Row k="Payout" v={r.payout_id} /> : null}
                  {r.booking_id ? <Row k="Booking" v={r.booking_id} /> : null}
                  {r.webhook_event_ids?.length ? <Row k="Webhook events" v={r.webhook_event_ids.join(", ")} /> : null}
                  {r.booking_status_before || r.booking_status_after ? (
                    <Row k="Booking status" v={`${r.booking_status_before ?? "—"} → ${r.booking_status_after ?? "—"}`} />
                  ) : null}
                  {r.payment_status_before || r.payment_status_after ? (
                    <Row k="Payment status" v={`${r.payment_status_before ?? "—"} → ${r.payment_status_after ?? "—"}`} />
                  ) : null}
                  {r.amount_total_pence != null ? <Row k="Amount total" v={`£${(r.amount_total_pence / 100).toFixed(2)}`} /> : null}
                  {r.application_fee_pence != null ? <Row k="App fee" v={`£${(r.application_fee_pence / 100).toFixed(2)}`} /> : null}
                  {r.helper_payout_pence != null ? <Row k="Helper payout" v={`£${(r.helper_payout_pence / 100).toFixed(2)}`} /> : null}
                </dl>

                <div className="mt-2 grid grid-cols-1 gap-x-6 text-xs text-muted-foreground sm:grid-cols-3">
                  <span>Checkout created: {fmt(r.checkout_created_at)}</span>
                  <span>Checkout completed: {fmt(r.checkout_completed_at)}</span>
                  <span>Webhook received: {fmt(r.webhook_received_at)}</span>
                  <span>Booking state changed: {fmt(r.booking_state_changed_at)}</span>
                  <span>Transfer created: {fmt(r.transfer_created_at)}</span>
                  <span>Payout paid: {fmt(r.payout_paid_at)}</span>
                </div>

                {r.notes ? <p className="mt-2 whitespace-pre-wrap text-xs">{r.notes}</p> : null}
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 ? (
          <div className="mt-6 flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" disabled={page === 0 || isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
            <span className="text-xs text-muted-foreground">Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children, required, full }: { label: string; children: React.ReactNode; required?: boolean; full?: boolean }) {
  return (
    <div className={full ? "sm:col-span-2" : undefined}>
      <Label className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}{required ? " *" : ""}
      </Label>
      {children}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-muted-foreground">{k}:</dt>
      <dd className="min-w-0 break-all font-mono">{v}</dd>
    </div>
  );
}

function PasteStripeCli({
  onParsed,
}: {
  onParsed: (parsed: ParsedStripeCli, mode: "fill-empty" | "overwrite") => void;
}) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<ParsedStripeCli | null>(null);
  const [mode, setMode] = useState<"fill-empty" | "overwrite">("fill-empty");

  const fieldCount = preview
    ? Object.values(preview).filter((v) => v !== undefined && !(Array.isArray(v) && v.length === 0)).length
    : 0;

  return (
    <div className="mb-5 rounded-xl border border-dashed bg-muted/30 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ClipboardPaste className="h-4 w-4" /> Paste Stripe CLI output
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-1">
            <input type="radio" name="paste-mode" checked={mode === "fill-empty"} onChange={() => setMode("fill-empty")} />
            Fill empty only
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" name="paste-mode" checked={mode === "overwrite"} onChange={() => setMode("overwrite")} />
            Overwrite
          </label>
        </div>
      </div>
      <Textarea
        rows={5}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setPreview(e.target.value.trim() ? parseStripeCliOutput(e.target.value) : null);
        }}
        placeholder={`Paste output from \`stripe listen\`, \`stripe trigger checkout.session.completed\`, or \`stripe events resolve evt_...\` here.\nWe'll extract cs_/pi_/ch_/tr_/po_/acct_/evt_ ids, amounts, and event timestamps.`}
        className="font-mono text-xs"
      />
      {preview ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Detected {fieldCount} field{fieldCount === 1 ? "" : "s"}
          {preview.webhook_event_ids?.length ? ` · ${preview.webhook_event_ids.length} event id${preview.webhook_event_ids.length === 1 ? "" : "s"}` : ""}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={async () => {
            try {
              const t = await navigator.clipboard.readText();
              setText(t);
              setPreview(t.trim() ? parseStripeCliOutput(t) : null);
            } catch {
              toast.error("Clipboard access blocked — paste manually");
            }
          }}
        >
          <ClipboardPaste className="mr-1.5 h-4 w-4" /> Read clipboard
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { setText(""); setPreview(null); }}
          disabled={!text}
        >
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!preview || fieldCount === 0}
          onClick={() => {
            if (!preview) return;
            onParsed(preview, mode);
            toast.success(`Auto-filled ${fieldCount} field${fieldCount === 1 ? "" : "s"}`);
          }}
        >
          <Wand2 className="mr-1.5 h-4 w-4" /> Apply to form
        </Button>
      </div>
    </div>
  );
}

